import React, { useEffect } from "react";

import { Toolbar } from "primereact/toolbar";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import timeout from "utils/timeout";
import { findObjectVal, normalizeString } from "utils/utils";
import { Path } from "react-hook-form";

type TableToolbarProps<T> = {
  searchData: T[];
  onSearch: (data: T[]) => void;
  searchFields: Path<T>[];
  labelBtn?: string;
  onClickBtn: () => void;
  classNameBtn?: string;
  showBtn?: boolean;
};

const TableToolbar = <T,>({
  searchData,
  onSearch,
  searchFields,
  labelBtn,
  classNameBtn,
  onClickBtn,
  showBtn = true,
}: TableToolbarProps<T>) => {
  return (
    <Toolbar
      left={<ToolbarSearch data={searchData} onSearch={onSearch} searchFields={searchFields} />}
      right={showBtn && <ToolbarButton label={labelBtn} className={classNameBtn} onClick={onClickBtn} />}
    />
  );
};

export default TableToolbar;

type ToolbarSearchProps<T> = {
  data: T[];
  onSearch: (data: T[]) => void;
  searchFields: Path<T>[];
};

const ToolbarSearch = <T,>({ data, onSearch, searchFields }: ToolbarSearchProps<T>) => {
  useEffect(() => {
    onSearch(data);
  }, [data]);

  const searchObject = (searchTerm: string) => {
    if (!searchTerm) return onSearch(data);
    const search = normalizeString(searchTerm.trim()).toLowerCase();
    const searchResults = data?.reduce((results: T[], obj: T) => {
      const values = searchFields.map(s => findObjectVal(obj, s));
      values.find(value => {
        if (value instanceof Object) {
          return Object.values(value).find(v => {
            if (v && normalizeString(v.toString()).toLowerCase().includes(search)) {
              results.push(obj);
              return true;
            }
            return false;
          });
        }
        if (value && normalizeString(value.toString()).toLowerCase().includes(search)) {
          results.push(obj);
          return true;
        } 
        return false;
      });
      return results;
    }, [] as T[]);
    return onSearch(searchResults);
  };
  
  return (
    <div className="p-toolbar-group-right">
      <div className="p-inputgroup">
        <span className="p-inputgroup-addon">
          <i className="pi pi-search" />
        </span>
        <InputText
          onChange={e => timeout(() => searchObject(e.target.value), 300)}
          placeholder="Search..."
        />
      </div>
    </div>
  );
};

type ToolbarButtonProps = {
  label?: string;
  className?: string;
  onClick: () => void;
};

const ToolbarButton: React.FC<ToolbarButtonProps> = ({ label, className, onClick }) => {
  return <Button type="button" icon="pi pi-plus" label={label} className={className} onClick={onClick} />;
};
